import { type PropsWithChildren, type RefObject, useMemo, useRef } from 'react'

import { FvButton, type IconProps, ValidatedForm } from '@fv/client-components'
import type { LabeledOption } from '@fv/client-components/combobox'
import {
  currencyToString,
  getApplicableTaxes,
  useCurrencyOptions,
} from '@fv/client-core'
import type { PropsWithLoad, SpotQuoteContact } from '@fv/client-types'
import type { VerySlimCarrier } from '@fv/models'
import { chargeNameLabel } from '@fv/models/core'

import { CarrierServiceTypeahead } from '@/components/carrier-service-types/CarrierServiceTypeSelector'
import InfoBox from '@/components/InfoBox'
import {
  InputAdornment,
  InputGroup,
  InputGroupWrapper,
} from '@/components/inputs/InputGroup'
import { RadioField } from '@/components/inputs/RadioField'
import { SelectField } from '@/components/inputs/SelectField'
import { ShipperAutocomplete } from '@/components/inputs/ShipperAutocomplete'
import { ShipperTypeahead } from '@/components/inputs/ShipperTypeahead'
import { Pinline } from '@/components/Pinline'
import { ShipperSlider } from '@/components/ShipperSlider'
import { useTypeNames } from '@/hooks/settings'

import { useSpotQuoteContacts } from '../spot-quote-contacts/queries'
import { useCarrierList } from './useCarrierList'
import { newQuoteId, useCreateQuote } from './useCreateQuote'
import {
  type ManualCarrierRate,
  type ManualRateType,
  type ManualSpotQuote,
  useManualRateForm,
} from './useManualRateForm'

type FormProps = {
  formRef: RefObject<HTMLFormElement>
}

const ManualRateForm = ({ formRef }: FormProps) => {
  const { actions, manualRate, load } = useManualRateForm()
  const carrierOptions = useCarrierOptions()
  if (load?.workflow !== 'ltl') return null

  const carrier = carrierOptions.list.find(
    c => c.value.name === manualRate.carrierName,
  )?.value

  const selectedCarrier =
    carrierOptions.list.find(c => c.value.id === manualRate.carrierId) ?? null

  const getInputProps = (
    name: keyof ManualCarrierRate,
    placeholder?: string,
  ) => ({
    name,
    onChange: actions.handleChange,
    placeholder,
    value: manualRate[name],
  })

  return (
    <div className="form-row mb-8">
      <InputGroupWrapper
        className="basis-1/2"
        label="Carrier"
        name="carrierName"
        required
      >
        <ShipperTypeahead
          classNames={{
            container: 'w-full',
          }}
          autoFocus
          isLoading={carrierOptions.loading}
          name="carrierName"
          onSelect={option => {
            actions.handleCarrierChange(option?.value ?? null, formRef.current)
          }}
          getOptionValue={c => c.value.id}
          options={carrierOptions.list}
          required
          value={selectedCarrier}
        />
      </InputGroupWrapper>

      <InputGroup
        className="basis-1/4"
        inputProps={{
          ...getInputProps('days', 'e.g. 3'),
          min: 1,
        }}
        inputType="amount"
        label="Days"
      />

      <InputGroup
        className="basis-1/4"
        inputProps={{
          ...getInputProps('price', 'e.g. 1200'),
          isPrice: true,
          min: 1,
        }}
        inputType="amount"
        label="Price"
        required
        startContent={<InputAdornment icon="dollar-sign" position="start" />}
      />

      <InputGroupWrapper
        className="basis-1/2"
        label="Service type"
        name="serviceType"
        required
      >
        <CarrierServiceTypeahead
          scac={carrier?.code}
          name="serviceType"
          value={manualRate.serviceId}
          type="select"
          onChange={v => actions.handleServiceChange(v)}
        />
      </InputGroupWrapper>

      <InputGroup
        className="basis-1/2"
        inputProps={getInputProps('quoteNumber')}
        inputType="text"
        label="Quote number"
        startContent={<InputAdornment icon="hashtag" position="start" />}
      />
    </div>
  )
}

const SpotQuoteForm = ({ formRef }: FormProps) => {
  const { actions, load, spotQuote } = useManualRateForm()
  const { equipmentName } = useTypeNames()
  const contactOptions = useContactOptions()
  const requiresQuoteNumber = spotQuote.contact?.requiresQuoteNumber ?? true
  const altEquipmentTypes = load?.equipment.alternateTypes ?? []
  const equipmentOptions = load?.equipment.type
    ? [load.equipment.type, ...altEquipmentTypes].map(t => ({
        text: equipmentName(t),
        value: t,
      }))
    : []
  const currencyOptions = useCurrencyOptions(load.locations)
  const getInputProps = (name: keyof Omit<ManualSpotQuote, 'contact'>) => ({
    name,
    onChange: actions.handleChange,
    value: spotQuote[name],
  })

  const taxBreakdown = getApplicableTaxes(
    spotQuote.currency,
    Number(spotQuote.price) || 0,
    load.locations,
  )

  return (
    <div className="form-row mb-8">
      <InputGroupWrapper
        className="basis-full"
        label="Email address"
        name="carrierEmail"
        required
      >
        <ShipperAutocomplete
          autoFocus
          classNames={{ container: 'w-full' }}
          isLoading={contactOptions.loading}
          name="carrierEmail"
          getOptionLabel={c => c.label}
          getOptionValue={c => c.value.email}
          onSelect={contact =>
            actions.handleContactChange(contact?.value ?? null, formRef.current)
          }
          onValueChange={val => actions.handleContactChange(val)}
          options={contactOptions.list}
          required
          value={spotQuote.carrierEmail}
        />
      </InputGroupWrapper>

      <InputGroup
        className="basis-1/2"
        inputProps={{
          ...getInputProps('carrierName'),
          readOnly: !!spotQuote.contact?.carrierName,
        }}
        inputType="text"
        label="Carrier name"
        required
      />

      <InputGroup
        className="basis-1/4"
        inputProps={getInputProps('quoteNumber')}
        inputType="text"
        label="Quote number"
        required={requiresQuoteNumber}
        startContent={<InputAdornment icon="hashtag" position="start" />}
      />

      <InputGroup
        className="basis-1/4"
        inputProps={{
          ...getInputProps('price'),
          isPrice: true,
          min: 1,
        }}
        inputType="amount"
        label="Price"
        required
        endContent={
          <SelectField
            name="currency"
            options={currencyOptions}
            {...getInputProps('currency')}
            className="w-auto -ml-px"
          />
        }
      />

      {!!altEquipmentTypes.length && (
        <InputGroup
          className="basis-full"
          inputProps={{
            ...getInputProps('equipmentType'),
            options: equipmentOptions,
          }}
          inputType="select"
          label="Equipment type"
          required
        />
      )}
      {taxBreakdown && (
        <div className="px-1 w-full">
          <div className="bg-fv-green-100 border-fv-green-300 border p-2 items-center flex gap-x-2 justify-end ">
            {taxBreakdown.taxes.map(t => (
              <div key={t.name} className="text-xs italic">
                <span>({chargeNameLabel(t.name)} = </span>
                <span>
                  ${currencyToString(t.amount)}{' '}
                  {spotQuote.currency.toUpperCase()})
                </span>
              </div>
            ))}
            <div className="flex items-center">
              <span>Total&nbsp;=&nbsp;</span>
              <span className="font-medium flex items-center gap-x-[.15rem]">
                <span className="font-oxygen">
                  ${currencyToString(taxBreakdown.total)}
                </span>
                <span className="text-xs self-start mt-[.155rem]">
                  {spotQuote.currency.toUpperCase()}
                </span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const rateTypeOptions: Array<{ label: string; value: ManualRateType }> = [
  {
    label: 'Manual rate',
    value: 'manual-rate',
  },
  {
    label: 'Spot quote',
    value: 'spot-quote',
  },
]

type PanelProps = {
  close?: () => void
}

export const ManualRatePanel = ({ close }: PanelProps) => {
  const { reset, setRateType } = useManualRateForm(s => s.actions)
  const formRef = useRef<HTMLFormElement>(null)
  const isOpen = useManualRateForm(s => s.isOpen)
  const load = useManualRateForm(s => s.load)
  const createQuote = useCreateQuote()
  const rateType = useManualRateForm(s => s.rateType)

  function onClose() {
    close?.()
    reset()
  }

  function onValidSubmit() {
    const loadId = load?.loadId
    if (!loadId) return

    const { manualRate, onError, onSuccess, spotQuote } =
      useManualRateForm.getState()

    createQuote(
      {
        loadId,
        quote: rateType === 'manual-rate' ? manualRate : spotQuote,
      },
      {
        ...(onError && { onError: () => onError(loadId) }),
        ...(onSuccess && {
          onSuccess: quote => {
            onSuccess(loadId, quote._id)
          },
        }),
      },
    )

    onSuccess?.(loadId, newQuoteId)
    onClose()
  }

  const rateTypeInput =
    load?.workflow === 'ltl' ? (
      <>
        <RadioField
          className="mb-6"
          name="rateType"
          onChange={setRateType}
          options={rateTypeOptions}
          value={rateType}
        />
        <Pinline rule />
      </>
    ) : null

  const FormComponent =
    rateType === 'manual-rate' ? ManualRateForm : SpotQuoteForm

  const panelContent = (
    <>
      {rateType === 'manual-rate' ? (
        <InfoBox className="mb-6">
          Manually entering a rate allows you to compare rates and/or book
          shipments with carriers that do not return automatic rates. This is
          great for smaller carriers or niche carriers that do not have auto
          rates returned inside of Freightview. After you enter information
          about the carrier's rate, you will then be able to choose it, book it,
          print labels and a BOL and capture analytical data.
        </InfoBox>
      ) : (
        <InfoBox className="mb-6">
          Manual entry is good for entering a quote that you and a
          carrier/broker negotiate over the phone, or outside of Freightview.
          You do not need to enter quotes manually for carriers/brokers that
          quote within our software.
        </InfoBox>
      )}

      {rateTypeInput}

      <ValidatedForm
        autoReportNextError={false}
        onValidSubmit={onValidSubmit}
        ref={formRef}
      >
        <FormComponent formRef={formRef} />

        <div className="flex w-full justify-end">
          <FvButton onClick={onClose} theme="plain" icon="times">
            Cancel
          </FvButton>
          <FvButton fwd theme="primary" type="submit" icon="check">
            Save {rateType === 'manual-rate' ? 'rate' : 'quote'}
          </FvButton>
        </div>
      </ValidatedForm>
    </>
  )

  if (close) return panelContent

  return (
    <ShipperSlider onClose={onClose} isOpen={isOpen}>
      <div>
        <h6>Manually enter a rate</h6>
        {panelContent}
      </div>
    </ShipperSlider>
  )
}

type AddRateButtonProps = PropsWithChildren<
  PropsWithLoad<{
    className?: string
    hasOwnPanel?: boolean
    icon?: IconProps['icon']
    onClick?: () => void
    onError?: (loadId: string) => void
    onSuccess?: (loadId: string, quoteId: string) => void
    rateType?: ManualRateType
  }>
>

export const AddRateButton = ({
  children,
  className,
  hasOwnPanel = false,
  icon,
  load,
  onClick,
  onError,
  onSuccess,
  rateType = 'spot-quote',
}: AddRateButtonProps) => {
  const { initializeForm } = useManualRateForm(s => s.actions)

  return (
    <FvButton
      className={className}
      icon={icon}
      onClick={() => {
        initializeForm({
          load,
          onError,
          onSuccess,
          openSlider: !hasOwnPanel,
          rateType,
        })

        onClick?.()
      }}
    >
      {children}
    </FvButton>
  )
}

function useCarrierOptions(): {
  loading: boolean
  list: LabeledOption<VerySlimCarrier>[]
} {
  const carriersQuery = useCarrierList('ltl')
  const list = useMemo(() => {
    return (carriersQuery.data ?? [])
      .filter(c => c.supportsManualRates)
      .map(c => ({
        label: c.name.trim(),
        value: c,
      }))
  }, [carriersQuery.data])

  return {
    list,
    loading: carriersQuery.isLoading,
  }
}

function useContactOptions(): {
  loading: boolean
  list: LabeledOption<SpotQuoteContact>[]
} {
  const contactsQuery = useSpotQuoteContacts()
  const list = useMemo(
    () =>
      (contactsQuery.data ?? [])
        .filter(c => c.email)
        .map(c => ({
          label: c.email.trim(),
          value: c,
        })),
    [contactsQuery.data],
  )

  return {
    list,
    loading: contactsQuery.isLoading,
  }
}
